import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authLogin, authRegister } from '../api';
import { toast } from 'sonner';
import { Wrench, Eye, EyeOff, MailCheck } from 'lucide-react';
import * as api from '../api';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator';

const GOOGLE_AUTH_URL = process.env.REACT_APP_GOOGLE_AUTH_URL;

function NotVerifiedPanel({ email, onBack }) {
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleResend = async () => {
    setLoading(true);
    try {
      await api.resendVerificationPublic(email);
      setSent(true);
    } catch {
      toast.error('Une erreur est survenue. Réessayez dans quelques instants.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="text-center space-y-5" data-testid="not-verified-panel">
      <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto"
        style={{ background: '#FEF3C7' }}>
        <MailCheck size={28} style={{ color: '#D97706' }} />
      </div>
      <div>
        <h2 className="text-2xl font-bold mb-1" style={{ color: 'var(--text)',  }}>
          Email non vérifié
        </h2>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
          Votre adresse <strong>{email}</strong> n'a pas encore été confirmée.<br />
          Vérifiez vos emails (y compris les spams) et cliquez sur le lien d'activation.
        </p>
      </div>

      {sent ? (
        <div className="p-4 rounded-xl text-sm font-medium"
          style={{ background: '#F0FDF4', color: '#15803D', border: '1px solid #BBF7D0' }}>
          Email renvoyé. Vérifiez votre boîte mail.
        </div>
      ) : (
        <button
          onClick={handleResend}
          disabled={loading}
          data-testid="resend-verification-btn"
          className="w-full py-3 rounded-lg font-semibold text-white text-sm transition-opacity disabled:opacity-60"
          style={{ background: 'var(--primary-gradient)' }}
        >
          {loading ? 'Envoi...' : 'Renvoyer l\'email de vérification'}
        </button>
      )}

      <button
        onClick={onBack}
        className="text-sm font-semibold"
        style={{ color: 'var(--primary-solid)' }}
      >
        ← Retour à la connexion
      </button>
    </div>
  );
}

export default function AuthPage() {
  const { setUser, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [isLogin, setIsLogin] = useState(location.pathname !== '/register');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [registerSent, setRegisterSent] = useState(false);
  const [notVerifiedEmail, setNotVerifiedEmail] = useState(null); // email bloqué — vérification en attente

  useEffect(() => {
    if (sessionStorage.getItem('mbud_session_expired') === '1') {
      sessionStorage.removeItem('mbud_session_expired');
      toast.error('Votre session a expiré. Veuillez vous reconnecter.');
    }
  }, []);

  const toggleMode = () => {
    const nextIsLogin = !isLogin;
    setIsLogin(nextIsLogin);
    navigate(nextIsLogin ? '/login' : '/register', { replace: true });
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const res = await authLogin({ email: form.email, password: form.password });
        localStorage.setItem('token', res.data.token);
        await checkAuth();
        navigate('/dashboard');
      } else {
        await authRegister(form);
        // Réponse uniforme côté backend — on affiche toujours l'écran de confirmation
        setRegisterSent(true);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (err.response?.status === 429) {
        toast.error('Trop de tentatives. Veuillez patienter.');
      } else if (detail === 'EMAIL_NOT_VERIFIED') {
        setNotVerifiedEmail(form.email);
      } else {
        toast.error(detail || 'Une erreur est survenue');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.forgotPassword(forgotEmail);
      setForgotSent(true);
    } catch (e) { 
      toast.error(e?.response?.data?.detail || 'Une erreur est survenue'); 
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `${GOOGLE_AUTH_URL}?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--bg)' }}>
      {/* Left panel - image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ background: 'var(--primary-gradient)' }}>
        <img
          src="https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Matériel"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <a href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.2)' }}>
              <Wrench size={22} />
            </div>
            <span className="text-2xl font-bold" style={{  }}>Bedee</span>
          </a>
          <div>
            <h2 className="text-4xl font-bold mb-4 leading-tight" style={{  }}>
              Gérez votre parc matériel simplement
            </h2>
            <p className="text-lg opacity-80" style={{  }}>
              Location, réparation, commerce — tout en un seul endroit.
            </p>
          </div>
          <div />
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <a href="/" className="flex items-center gap-2 mb-8 lg:hidden hover:opacity-80 transition-opacity">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--primary-gradient)' }}>
              <Wrench size={18} className="text-white" />
            </div>
            <span className="text-xl font-bold" style={{ color: 'var(--primary-solid)',  }}>Bedee</span>
          </a>

          {/* Email non vérifié */}
          {notVerifiedEmail ? (
            <NotVerifiedPanel
              email={notVerifiedEmail}
              onBack={() => setNotVerifiedEmail(null)}
            />
          ) : registerSent ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto"
                style={{ background: 'var(--primary-50)' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--primary-solid)" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.5 19.79 19.79 0 0 1 1.62 4.9 2 2 0 0 1 3.6 2.68h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 10a16 16 0 0 0 6.09 6.09l.95-1.95a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
              </div>
              <h2 className="text-2xl font-bold" style={{ color: 'var(--text)',  }}>
                Vérifiez votre email
              </h2>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                Si cette adresse n'est pas déjà utilisée, vous recevrez un email de confirmation.<br />
                Cliquez sur le lien pour activer votre compte.
              </p>
              <button
                onClick={() => { setRegisterSent(false); setIsLogin(true); navigate('/login', { replace: true }); }}
                className="w-full py-3 rounded-lg font-semibold text-white text-sm"
                style={{ background: 'var(--primary-gradient)' }}
              >
                Aller à la connexion
              </button>
            </div>
          ) : forgotMode ? (
            <div>
              <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--primary-solid)',  }}>
                Mot de passe oublié
              </h1>
              <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>
                Entrez votre email pour recevoir un lien de réinitialisation.
              </p>
              {forgotSent ? (
                <div className="p-4 rounded-lg text-sm" style={{ background: 'var(--primary-100)', color: 'var(--primary-solid)' }}>
                  Si un compte existe avec cet email, vous recevrez un lien de réinitialisation.
                </div>
              ) : (
                <form onSubmit={handleForgot} className="space-y-4">
                  <input
                    data-testid="forgot-email-input"
                    type="email" required value={forgotEmail}
                    onChange={e => setForgotEmail(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none"
                    style={{ borderColor: 'var(--text-disabled)' }} placeholder="votre@email.com"
                  />
                  <button data-testid="forgot-submit-btn" type="submit" disabled={loading}
                    className="w-full py-3 rounded-lg font-semibold text-white text-sm"
                    style={{ background: loading ? 'var(--primary-light)' : 'var(--primary-gradient)' }}>
                    {loading ? 'Envoi...' : 'Envoyer le lien'}
                  </button>
                </form>
              )}
              <button onClick={() => { setForgotMode(false); setForgotSent(false); }}
                className="mt-4 text-sm font-semibold" style={{ color: 'var(--primary-solid)' }}>
                ← Retour à la connexion
              </button>
            </div>
          ) : (
          <div>
          <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--primary-solid)',  }}>
            {isLogin ? 'Connexion' : 'Inscription'}
          </h1>
          <p className="text-sm mb-8" style={{ color: 'var(--text-muted)',  }}>
            {isLogin ? 'Heureux de vous revoir !' : 'Créez votre compte Bedee'}
          </p>

          {/* Google button — désactivé temporairement */}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)',  }}>Nom</label>
                <input
                  data-testid="register-name-input"
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none transition-all"
                  style={{ borderColor: 'var(--text-disabled)',  }}
                  placeholder="Jean Dupont"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)',  }}>Email</label>
              <input
                data-testid="auth-email-input"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none transition-all"
                style={{ borderColor: 'var(--text-disabled)',  }}
                placeholder="votre@email.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)',  }}>Mot de passe</label>
              <div className="relative">
                <input
                  data-testid="auth-password-input"
                  type={showPw ? 'text' : 'password'}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none transition-all pr-10"
                  style={{ borderColor: 'var(--text-disabled)',  }}
                  placeholder="••••••••"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-3" style={{ color: 'var(--text-placeholder)' }}>
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {!isLogin && <PasswordStrengthIndicator password={form.password} />}
            </div>
            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg font-semibold text-white text-sm transition-all active:scale-95"
              style={{ background: loading ? 'var(--primary-light)' : 'var(--primary-gradient)',  }}
            >
              {loading ? 'Chargement...' : isLogin ? 'Connexion' : 'Inscription'}
            </button>
          </form>

          <p className="mt-4 text-center text-sm" style={{ color: 'var(--text-muted)',  }}>
            {isLogin ? "Pas encore de compte ?" : 'Déjà un compte ?'}{' '}
            <button
              data-testid="auth-toggle-btn"
              onClick={toggleMode}
              className="font-semibold"
              style={{ color: 'var(--primary-solid)' }}
            >
              {isLogin ? 'Inscription' : 'Connexion'}
            </button>
          </p>

          {isLogin && (
            <p className="mt-2 text-center text-sm">
              <button data-testid="forgot-password-link" onClick={() => setForgotMode(true)}
                className="font-medium" style={{ color: 'var(--text-muted)' }}>
                Mot de passe oublié ?
              </button>
            </p>
          )}

          </div>
          )}
        </div>
      </div>
    </div>
  );
}
